import {
  Box,
  Button,
  Chip,
  Link as MuiLink,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { useAuth } from '../../auth/AuthProvider';
import type { DataSource } from '../../config/capabilities';
import { useRepository } from '../../data/RepositoryProvider';
import { useResource } from '../../hooks/useResource';
import { paths } from '../../routes/paths';
import { DataSourceBadge } from '../../shared/data/DataSourceBadge';
import { PageState } from '../../shared/data/PageState';
import { formatDecimalString } from '../../shared/format/decimal';
import { formatTimestamp, type TimeLanguage } from '../../shared/format/time';
import { PageHeader } from '../../shared/layout/PageHeader';
import { PartnerGameDialog, type PartnerGameDialogMode } from './PartnerGameDialog';
import { toSafePartnerGameError } from './partnerGameError';
import { gamesRepositoryToken } from './repository';
import type { PartnerGame } from './types';

interface DialogState {
  readonly mode: PartnerGameDialogMode;
  readonly partnerGame?: PartnerGame | undefined;
}

export function PartnerGamesPanel({ dataSource }: { readonly dataSource: DataSource }) {
  const { partnerId = '' } = useParams();
  const repository = useRepository(gamesRepositoryToken);
  const auth = useAuth();
  const { i18n, t } = useTranslation();
  const language: TimeLanguage = i18n.language.startsWith('th') ? 'th' : 'en';
  const [refreshKey, setRefreshKey] = useState(0);
  const [dialog, setDialog] = useState<DialogState | null>(null);
  const resource = useResource(
    (signal) => repository.listPartnerGames(partnerId, signal),
    [partnerId, refreshKey, repository],
    (items) => items.length === 0,
  );
  const canManage =
    auth.status === 'authenticated' && auth.principal.permissions.includes('GAME_MANAGE');
  const canViewPartners =
    auth.status === 'authenticated' && auth.principal.permissions.includes('PARTNER_VIEW');
  const refresh = () => setRefreshKey((key) => key + 1);

  return (
    <Box sx={{ minWidth: 0 }}>
      <PageHeader
        actions={
          <>
            <DataSourceBadge source={dataSource} />
            {canManage ? (
              <Button onClick={() => setDialog({ mode: 'create' })} variant="contained">
                {t('games.partner.actions.configure')}
              </Button>
            ) : null}
          </>
        }
        subtitle={t('games.partner.subtitle', { partnerId })}
        title={t('games.partner.title')}
      />
      {canViewPartners ? (
        <MuiLink component={Link} to={`${paths.partners}/${encodeURIComponent(partnerId)}`}>
          {t('games.partner.actions.backToPartner')}
        </MuiLink>
      ) : null}
      <Box sx={{ mt: 2 }}>
        <PageState
          emptyMessage={t('games.partner.empty')}
          error={
            resource.error === null
              ? null
              : toSafePartnerGameError(resource.error, t, t('games.partner.errors.load'))
          }
          onRetry={refresh}
          status={resource.status}
        >
          {resource.data === null ? null : (
            <PartnerGamesTable
              canManage={canManage}
              items={resource.data}
              language={language}
              onEdit={(partnerGame) => setDialog({ mode: 'edit', partnerGame })}
              onStatus={(partnerGame) => setDialog({ mode: 'status', partnerGame })}
            />
          )}
        </PageState>
      </Box>
      {dialog === null ? null : (
        <PartnerGameDialog
          mode={dialog.mode}
          onClose={() => setDialog(null)}
          onSaved={() => refresh()}
          partnerGame={dialog.partnerGame}
          partnerId={partnerId}
        />
      )}
    </Box>
  );
}

function PartnerGamesTable({
  canManage,
  items,
  language,
  onEdit,
  onStatus,
}: {
  readonly canManage: boolean;
  readonly items: readonly PartnerGame[];
  readonly language: TimeLanguage;
  readonly onEdit: (partnerGame: PartnerGame) => void;
  readonly onStatus: (partnerGame: PartnerGame) => void;
}) {
  const { t } = useTranslation();
  return (
    <Paper variant="outlined">
      <TableContainer
        aria-label={t('games.partner.tableLabel')}
        role="region"
        sx={{ maxWidth: '100%', overflowX: 'auto' }}
        tabIndex={0}
      >
        <Table size="small" sx={{ minWidth: 1040 }}>
          <TableHead>
            <TableRow>
              <TableCell>{t('games.partner.fields.game')}</TableCell>
              <TableCell>{t('games.fields.name')}</TableCell>
              <TableCell>{t('games.fields.gameType')}</TableCell>
              <TableCell>{t('games.partner.fields.currency')}</TableCell>
              <TableCell align="right">{t('games.partner.fields.maximumPlayAmount')}</TableCell>
              <TableCell>{t('games.fields.status')}</TableCell>
              <TableCell>{t('games.partner.fields.effectiveStatus')}</TableCell>
              <TableCell>{t('games.fields.dependencies')}</TableCell>
              <TableCell>{t('games.fields.updatedAt')}</TableCell>
              {canManage ? <TableCell>{t('common.actions')}</TableCell> : null}
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => {
              const terminal = item.game.status === 'CLOSED';
              return (
                <TableRow key={`${item.partnerId}-${item.game.id}`} hover>
                  <TableCell>
                    <MuiLink component={Link} to={`${paths.games}/${item.game.id}`}>
                      {item.game.code}
                    </MuiLink>
                  </TableCell>
                  <TableCell>{item.game.name}</TableCell>
                  <TableCell>
                    <MuiLink component={Link} to={`/game-types/${item.game.gameType.id}`}>
                      {item.game.gameType.code}
                    </MuiLink>
                  </TableCell>
                  <TableCell>{item.currencyCode}</TableCell>
                  <TableCell align="right">
                    {formatDecimalString(item.maximumPlayAmount, 6)}
                  </TableCell>
                  <TableCell>
                    <PartnerGameStatusChip status={item.status} />
                  </TableCell>
                  <TableCell>
                    <PartnerGameStatusChip status={item.effectiveStatus} />
                  </TableCell>
                  <TableCell sx={{ overflowWrap: 'anywhere' }}>
                    {item.dependencyBlockers.length === 0
                      ? t('games.detail.noDependencies')
                      : item.dependencyBlockers.join(', ')}
                  </TableCell>
                  <TableCell>
                    {formatTimestamp(item.updatedAt, 'Asia/Bangkok', language).display}
                  </TableCell>
                  {canManage ? (
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Button disabled={terminal} onClick={() => onEdit(item)} size="small">
                          {t('games.partner.actions.edit')}
                        </Button>
                        <Button disabled={terminal} onClick={() => onStatus(item)} size="small">
                          {t('games.partner.actions.changeStatus')}
                        </Button>
                      </Stack>
                    </TableCell>
                  ) : null}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Typography color="text.secondary" sx={{ px: 2, py: 1 }} variant="caption">
        {t('games.partner.resultCount', { count: items.length })}
      </Typography>
    </Paper>
  );
}

function PartnerGameStatusChip({ status }: { readonly status: string }) {
  return (
    <Chip
      color={status === 'ACTIVE' ? 'success' : status === 'SUSPENDED' ? 'warning' : 'default'}
      label={status}
      size="small"
    />
  );
}
